import type { ReelTranscriptStructureView } from './ReelTranscriptStructureContext';
import type {
  ReelWordStamp,
  StructureTokenLike,
} from '@src/services/reel/reelTranscriptStructure';

/**
 * PLAT-11159 — expand-then-look, in one place.
 *
 * A collapsed passage's words are off the page, and every mechanism that
 * lands on a word by position — search, the playback cursor, a restored
 * position, the Reel Editor's jump — silently does nothing when its target
 * is missing. The rule is to expand BEFORE looking, never to retry after a
 * failure: a lookup that resolved nothing is indistinguishable from a dozen
 * other causes.
 *
 * Returns whether anything was folded over the word (and is now opening) so
 * the caller knows its DOM target does not exist yet this frame and can
 * defer its lookup to after the commit. No-ops — cheaply, before any token
 * walk — when no host publishes collapse, or nothing is folded.
 */
export function revealCollapsedWord(
  view: ReelTranscriptStructureView | null,
  elements: readonly StructureTokenLike[] | undefined,
  index: number,
  precomputedStamp?: ReelWordStamp,
  reason?: 'playback' | 'search' | 'jump' | 'mount',
): boolean {
  const collapse = view?.collapse;
  if (!view || !collapse || !elements) return false;
  const anythingFolded =
    collapse.collapsedChapterIds.size > 0 ||
    collapse.collapsedSectionIds.size > 0;
  if (!anythingFolded) return false;

  const resolved = resolveReelWordSection(
    view,
    elements,
    index,
    precomputedStamp,
  );
  if (!resolved?.hidden) return false;

  collapse.revealSection(resolved.sectionId, resolved.chapterId, reason);
  return true;
}

/**
 * PLAT-11163 — which Section a word belongs to, and whether it is currently
 * folded away. The jump uses this to land on the Section's HEADING rather
 * than a bare word, and the reveal above uses it to know what to open.
 */
export function resolveReelWordSection(
  view: ReelTranscriptStructureView | null,
  elements: readonly StructureTokenLike[] | undefined,
  index: number,
  precomputedStamp?: ReelWordStamp,
): { sectionId: string; chapterId?: string; hidden: boolean } | null {
  if (!view || !elements) return null;
  const stamp = precomputedStamp ?? view.stampWords(elements);
  const sectionId = stamp.sectionIdByWordIndex.get(index);
  if (sectionId === undefined) return null;

  const chapter = view.structure.chapters.find(candidate =>
    candidate.sections.some(section => section.sectionId === sectionId),
  );
  const collapse = view.collapse;
  const hidden =
    !!collapse &&
    ((chapter !== undefined &&
      collapse.collapsedChapterIds.has(chapter.chapterId)) ||
      collapse.collapsedSectionIds.has(sectionId));
  return { sectionId, chapterId: chapter?.chapterId, hidden };
}
